// src/pages/driver-management/AddScoreConfigModal.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Slider,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  LinearProgress
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import FormDialog from '../../components/FormDialog';
import PickerField from '../../components/PickerField';
import { useCompanyDataList, useCompanyList } from '../../stores/companyStore';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useNotifier } from '../../components/Notifier';
import { useLanguage } from '../../utils/i18n';
import { createDriverScoreConfig, updateDriverScoreConfig, getDriverScoreConfigById } from '../../stores/driverScoreConfigStore';

// Source groups: which device/system produces each event type.
// Order here also controls the display order of the grouped sections in the UI.
export const SOURCE_GROUPS = [
  { value: 'gps-telematik', label: 'GPS Telematik', labelKey: 'driverScore.sourceGpsTelematik', color: 'primary', gridMd: 3 },
  { value: 'vss-camera', label: 'VSS Camera', labelKey: 'driverScore.sourceVssCamera', color: 'secondary', gridMd: 4 }
];

export const getSourceGroupLabel = (t, sourceValue) => {
  const group = SOURCE_GROUPS.find((g) => g.value === sourceValue);
  if (!group) return String(sourceValue || '');
  return t ? t(group.labelKey, group.label) : group.label;
};

export const EVENT_TYPE_OPTIONS = [
  // GPS Telematik
  { value: 'harsh_braking', label: 'Harsh Braking', labelKey: 'driverScore.eventHarshBraking', color: 'error', source: 'gps-telematik' },
  {
    value: 'harsh_acceleration',
    label: 'Harsh Acceleration',
    labelKey: 'driverScore.eventHarshAcceleration',
    color: 'warning',
    source: 'gps-telematik'
  },
  { value: 'over_speed', label: 'Over Speed', labelKey: 'driverScore.eventOverSpeed', color: 'error', source: 'gps-telematik' },
  { value: 'idle_time', label: 'Idle Time', labelKey: 'driverScore.eventIdleTime', color: 'info', source: 'gps-telematik' },
  // VSS Camera
  { value: 'yawning', label: 'Yawning', labelKey: 'driverScore.eventYawning', color: 'warning', source: 'vss-camera' },
  { value: 'smoking', label: 'Smoking', labelKey: 'driverScore.eventSmoking', color: 'error', source: 'vss-camera' },
  { value: 'sleepy', label: 'Sleepy', labelKey: 'driverScore.eventSleepy', color: 'error', source: 'vss-camera' },
  { value: 'distraction', label: 'Distraction', labelKey: 'driverScore.eventDistraction', color: 'warning', source: 'vss-camera' },
  { value: 'phone_call', label: 'Phone Call', labelKey: 'driverScore.eventPhoneCall', color: 'warning', source: 'vss-camera' },
  { value: 'covering_camera', label: 'Covering Camera', labelKey: 'driverScore.eventCoveringCamera', color: 'error', source: 'vss-camera' }
];

export const getEventTypeLabel = (t, itemOrValue) => {
  const item = typeof itemOrValue === 'object' ? itemOrValue : EVENT_TYPE_OPTIONS.find((opt) => opt.value === itemOrValue);
  if (!item) return String(itemOrValue || '');
  return t ? t(item.labelKey, item.label) : item.label;
};

const DEFAULT_PARAMETERS = EVENT_TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = 10;
  return acc;
}, {});

const DEFAULT_SCORE_INDEXES = [
  { idx_name: 'A', min_idx: 91, max_idx: 100, min_dist_km: 0 },
  { idx_name: 'B', min_idx: 71, max_idx: 90, min_dist_km: 0 },
  { idx_name: 'C', min_idx: 51, max_idx: 70, min_dist_km: 0 },
  { idx_name: 'D', min_idx: 31, max_idx: 50, min_dist_km: 0 },
  { idx_name: 'E', min_idx: 0, max_idx: 30, min_dist_km: 0 }
];

// Helper: generate next sequential letter (A, B, C, ... Z, AA, AB, ...) based on current row count
function generateIndexLabel(position) {
  let n = position;
  let label = '';
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

function SliderTooltip(props) {
  const { children, value } = props;
  const { t } = useLanguage();
  const formattedVal = typeof value === 'number' ? value.toFixed(2) : Number(value || 0).toFixed(2);

  return (
    <Tooltip
      enterTouchDelay={0}
      placement="top"
      title={`${t('general.value', 'Value')}: ${formattedVal}% (${t('general.min', 'Min')}: 0%, ${t('general.max', 'Max')}: 100%)`}
      arrow
    >
      {children}
    </Tooltip>
  );
}

SliderTooltip.propTypes = {
  children: PropTypes.element.isRequired,
  value: PropTypes.number
};

export default function AddScoreConfigModal({ open, id = null, initialData = null, onClose, onSuccess }) {
  const { t } = useLanguage();
  const notify = useNotifier();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [minDistKm, setMinDistKm] = useState('');
  const [pctWarnLimit, setPctWarnLimit] = useState(0);
  const [pctWarnLimitInput, setPctWarnLimitInput] = useState('0');
  const [parameters, setParameters] = useState(DEFAULT_PARAMETERS);
  const [scoreIndexes, setScoreIndexes] = useState(DEFAULT_SCORE_INDEXES);
  const [formErrors, setFormErrors] = useState({});

  // Company picker pagination
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const { companyOptions } = useCompanyList();

  const combinedCompanyOptions = useMemo(() => {
    const list = companyPickerParams.options && companyPickerParams.options.length > 0 ? companyPickerParams.options : companyOptions;
    return (list || []).map((c) => ({
      value: String(c.value ?? c.id ?? ''),
      label: c.label ?? c.name ?? String(c.value ?? c.id ?? ''),
      raw: c._raw ?? c
    }));
  }, [companyPickerParams.options, companyOptions]);

  // Calculate sum of parameter weights
  const totalParamWeight = useMemo(() => {
    return Object.values(parameters).reduce((acc, val) => acc + Number(val || 0), 0);
  }, [parameters]);

  const isParamWeight100 = Math.abs(totalParamWeight - 100) < 0.01;

  // Reset or fetch initial values when opened
  useEffect(() => {
    if (!open) {
      setFormErrors((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    let isMounted = true;
    const initForm = async () => {
      setFormErrors({});
      if (initialData || id) {
        setLoading(!initialData);
        try {
          const data = initialData || (id ? await getDriverScoreConfigById(id) : null);
          if (!isMounted) return;
          if (data) {
            setCompanyId(String(data.company ?? data.company_id ?? ''));
            setCompanyName(data.company_name ?? data.join?.company_name ?? '');
            setName(data.name ?? '');
            setMinDistKm(data.min_dist_km != null ? String(data.min_dist_km) : '');
            const warnVal = data.pct_warn_limit != null ? Number(data.pct_warn_limit) : 0;
            setPctWarnLimit(warnVal);
            setPctWarnLimitInput(warnVal.toFixed(2));

            // Populate parameters/events
            const loadedParams = { ...DEFAULT_PARAMETERS };
            if (data.events && typeof data.events === 'object' && !Array.isArray(data.events)) {
              Object.keys(data.events).forEach((k) => {
                const normalizedKey = k.replace(/-/g, '_');
                if (loadedParams[normalizedKey] !== undefined) {
                  loadedParams[normalizedKey] = Number(data.events[k] ?? 0);
                } else if (loadedParams[k] !== undefined) {
                  loadedParams[k] = Number(data.events[k] ?? 0);
                }
              });
            } else if (Array.isArray(data.parameters)) {
              data.parameters.forEach((p) => {
                const rawKey = p.parameter_name || p.key || p.name || '';
                const key = rawKey.replace(/-/g, '_');
                if (key && loadedParams[key] !== undefined) {
                  loadedParams[key] = Number(p.value ?? 0);
                }
              });
            } else if (data.parameters && typeof data.parameters === 'object') {
              Object.keys(data.parameters).forEach((k) => {
                const normalizedKey = k.replace(/-/g, '_');
                if (loadedParams[normalizedKey] !== undefined) {
                  loadedParams[normalizedKey] = Number(data.parameters[k] ?? 0);
                }
              });
            }
            setParameters(loadedParams);

            const rawIndexes =
              Array.isArray(data.score_indexes) && data.score_indexes.length > 0
                ? data.score_indexes
                : Array.isArray(data.idx_detail) && data.idx_detail.length > 0
                  ? data.idx_detail
                  : [];

            if (rawIndexes.length > 0) {
              setScoreIndexes(
                rawIndexes.map((idx) => ({
                  ...(idx.id != null && { id: Number(idx.id) }),
                  idx_name: idx.idx_name ?? '',
                  max_idx: idx.max_idx != null ? Number(idx.max_idx) : 0,
                  min_dist_km: idx.min_dist_km != null ? Number(idx.min_dist_km) : data.min_dist_km != null ? Number(data.min_dist_km) : 0,
                  min_idx: idx.min_idx != null ? Number(idx.min_idx) : 0
                }))
              );
            } else {
              setScoreIndexes(DEFAULT_SCORE_INDEXES);
            }
          }
        } catch (err) {
          console.error('Failed to load score config detail:', err);
          notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
        } finally {
          if (isMounted) setLoading(false);
        }
      } else {
        // Defaults for Add mode
        let defaultCompId = '';
        let defaultCompName = '';
        try {
          const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
          if (stored && stored !== 'all') {
            defaultCompId = String(stored);
            const found = combinedCompanyOptions.find((c) => String(c.value) === String(stored));
            if (found) defaultCompName = found.label;
          }
        } catch {}

        setCompanyId(defaultCompId);
        setCompanyName(defaultCompName);
        setName('');
        setMinDistKm('');
        setPctWarnLimit(0);
        setPctWarnLimitInput('0');
        setParameters(DEFAULT_PARAMETERS);
        setScoreIndexes(DEFAULT_SCORE_INDEXES);
      }
    };

    initForm();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id, initialData, combinedCompanyOptions]);

  // Sync Slider and Input Box for Warning Threshold
  const handleSliderChange = (event, newValue) => {
    const val = typeof newValue === 'number' ? newValue : Number(newValue);
    setPctWarnLimit(val);
    setPctWarnLimitInput(val.toFixed(2));
    if (formErrors.pct_warn_limit) {
      setFormErrors((prev) => ({ ...prev, pct_warn_limit: null }));
    }
  };

  const handlePctInputChange = (e) => {
    const rawVal = e.target.value;
    setPctWarnLimitInput(rawVal);
    const parsed = parseFloat(rawVal);
    if (!isNaN(parsed)) {
      const clamped = Math.max(0, Math.min(100, parsed));
      setPctWarnLimit(clamped);
    }
    if (formErrors.pct_warn_limit) {
      setFormErrors((prev) => ({ ...prev, pct_warn_limit: null }));
    }
  };

  const handlePctInputBlur = () => {
    const parsed = parseFloat(pctWarnLimitInput);
    if (isNaN(parsed) || parsed < 0) {
      setPctWarnLimit(0);
      setPctWarnLimitInput('0.00');
    } else if (parsed > 100) {
      setPctWarnLimit(100);
      setPctWarnLimitInput('100.00');
    } else {
      setPctWarnLimit(parsed);
      setPctWarnLimitInput(parsed.toFixed(2));
    }
  };

  // Parameter Weight Change Handlers
  const handleParamChange = (key, val) => {
    const num = val === '' ? 0 : Number(val);
    setParameters((prev) => ({
      ...prev,
      [key]: Math.max(0, Math.min(100, num))
    }));
    if (formErrors.parameters) {
      setFormErrors((prev) => ({ ...prev, parameters: null }));
    }
  };

  const handleDistributeEvenly = () => {
    const count = EVENT_TYPE_OPTIONS.length;
    const evenVal = Number((100 / count).toFixed(2));
    const updated = {};
    EVENT_TYPE_OPTIONS.forEach((opt) => {
      updated[opt.value] = evenVal;
    });
    setParameters(updated);
  };

  const handleResetParams = () => {
    const reset = {};
    EVENT_TYPE_OPTIONS.forEach((opt) => {
      reset[opt.value] = 0;
    });
    setParameters(reset);
  };

  // Score Indexes Table Handlers
  // NOTE: idx_name is no longer manually editable — it's auto-generated (A, B, C, ...)
  // and only min_idx / max_idx remain editable.
  const handleIndexChange = (index, field, value) => {
    if (field === 'idx_name') return; // locked field, ignore
    const updated = [...scoreIndexes];
    const num = value === '' ? '' : Number(value);
    updated[index] = { ...updated[index], [field]: num };
    setScoreIndexes(updated);
    if (formErrors.score_indexes) {
      setFormErrors((prev) => ({ ...prev, score_indexes: null }));
    }
  };

  const handleAddIndexRow = () => {
    setScoreIndexes((prev) => [
      ...prev,
      {
        idx_name: generateIndexLabel(prev.length),
        max_idx: 0,
        min_dist_km: minDistKm !== '' && !isNaN(Number(minDistKm)) ? Number(minDistKm) : 0,
        min_idx: 0
      }
    ]);
    if (formErrors.score_indexes) {
      setFormErrors((prev) => ({ ...prev, score_indexes: null }));
    }
  };

  const handleDeleteIndexRow = (index) => {
    if (scoreIndexes.length <= 1) {
      notify({ message: t('driverScore.minOneIndex', 'At least 1 score index is required!'), severity: 'warning' });
      return;
    }
    setScoreIndexes((prev) => {
      const next = prev.filter((_, i) => i !== index);
      // Re-sequence labels (A, B, C, ...) after removal so they stay contiguous
      return next.map((row, i) => ({ ...row, idx_name: generateIndexLabel(i) }));
    });
    if (formErrors.score_indexes) {
      setFormErrors((prev) => ({ ...prev, score_indexes: null }));
    }
  };

  // Score Indexes Validation Check
  const validateScoreIndexes = useMemo(() => {
    if (!scoreIndexes || scoreIndexes.length === 0) {
      return t('driverScore.indexCannotEmpty', 'Score indexes table cannot be empty.');
    }

    for (let i = 0; i < scoreIndexes.length; i++) {
      const row = scoreIndexes[i];
      const name = String(row.idx_name || '').trim();
      if (!name) {
        return t('driverScore.indexNameRequired', `Index label on row ${i + 1} cannot be empty.`);
      }
      if (row.min_idx === '' || isNaN(row.min_idx) || row.min_idx < 0 || row.min_idx > 100) {
        return t('driverScore.indexMinRange', `Min score for index '${name}' must be between 0 and 100.`);
      }
      if (row.max_idx === '' || isNaN(row.max_idx) || row.max_idx < 0 || row.max_idx > 100) {
        return t('driverScore.indexMaxRange', `Max score for index '${name}' must be between 0 and 100.`);
      }
      if (Number(row.min_idx) > Number(row.max_idx)) {
        return t(
          'driverScore.indexMinGreaterMax',
          `Min score (${row.min_idx}) cannot be greater than Max score (${row.max_idx}) on index '${name}'.`
        );
      }
    }

    for (let i = 0; i < scoreIndexes.length; i++) {
      for (let j = i + 1; j < scoreIndexes.length; j++) {
        const a = scoreIndexes[i];
        const b = scoreIndexes[j];
        const minA = Number(a.min_idx);
        const maxA = Number(a.max_idx);
        const minB = Number(b.min_idx);
        const maxB = Number(b.max_idx);

        if (Math.max(minA, minB) <= Math.min(maxA, maxB)) {
          return t(
            'driverScore.indexOverlap',
            `Index range '${a.idx_name}' (${minA}-${maxA}) overlaps with index '${b.idx_name}' (${minB}-${maxB}). Please make sure ranges are unique and do not overlap.`
          );
        }
      }
    }

    return null;
  }, [scoreIndexes, t]);

  // Form Submit Handler
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const errors = {};
    if (!companyId) {
      errors.company = t('validation.companyRequired', 'Company is required.');
    }
    if (!name || name.trim() === '') {
      errors.name = t('validation.nameRequired', 'Name is required.');
    }
    if (minDistKm === '' || isNaN(Number(minDistKm)) || Number(minDistKm) < 0) {
      errors.min_dist_km = t('driverScore.minDistRequired', 'Minimum distance must be a positive number.');
    }
    if (isNaN(pctWarnLimit) || pctWarnLimit < 0 || pctWarnLimit > 100) {
      errors.pct_warn_limit = t('driverScore.warnLimitRange', 'Warning threshold must be between 0% and 100%.');
    }
    if (validateScoreIndexes) {
      errors.score_indexes = validateScoreIndexes;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      notify({ message: t('driverScore.formInvalid', 'Please check the form for errors.'), severity: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        company: Number(companyId),
        company_name: companyName || '',
        events: {
          over_speed: Number(parameters.over_speed ?? 0),
          harsh_braking: Number(parameters.harsh_braking ?? 0),
          harsh_acceleration: Number(parameters.harsh_acceleration ?? 0),
          idle_time: Number(parameters.idle_time ?? 0),
          yawning: Number(parameters.yawning ?? 0),
          smoking: Number(parameters.smoking ?? 0),
          sleepy: Number(parameters.sleepy ?? 0),
          distraction: Number(parameters.distraction ?? 0),
          phone_call: Number(parameters.phone_call ?? 0),
          covering_camera: Number(parameters.covering_camera ?? 0)
        },
        min_dist_km: Number(minDistKm),
        name: name.trim(),
        pct_warn_limit: Number(pctWarnLimit),
        score_indexes: scoreIndexes.map((idx) => ({
          ...(idx.id != null && Number(idx.id) > 0 ? { id: Number(idx.id) } : {}),
          idx_name: String(idx.idx_name).trim(),
          max_idx: Number(idx.max_idx),
          min_dist_km: Number(idx.min_dist_km != null ? idx.min_dist_km : minDistKm || 0),
          min_idx: Number(idx.min_idx)
        }))
      };

      if (id) {
        await updateDriverScoreConfig(id, payload);
        notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
      } else {
        await createDriverScoreConfig(payload);
        notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
      }

      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (err) {
      console.error('Submit score config error:', err);
      const errMsg = err?.response?.data?.message || err?.message || t('notify.saveFailed', 'Failed to save data');
      notify({ message: errMsg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const dialogActions = (
    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ width: '100%', px: 2 }}>
      <Button variant="outlined" onClick={onClose} disabled={submitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={submitting || loading}
        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 4,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {submitting ? t('buttonTitle.processing', 'Processing...') : id ? t('buttonTitle.save', 'Save') : t('buttonTitle.add', 'Add')}
      </Button>
    </Stack>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={
        id
          ? `${t('general.edit', 'Edit')} ${t('driverScore.scoreSetting', 'Score Setting')}`
          : `${t('general.add', 'Add')} ${t('driverScore.scoreSetting', 'Score Setting')}`
      }
      subtitle={t('driverScore.modalSubtitle', 'Configure threshold parameters, parameter weights, and score indexes')}
      fullScreenMode={true}
      actions={dialogActions}
    >
      {/* Wider container so full-screen space is actually used, no more centered narrow column */}
      <Box sx={{ width: '100%', maxWidth: 1800, p: { xs: 1, md: 2 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {/* Two-column layout: left = general info + score indexes, right = event parameters */}
            <Grid container spacing={3}>
              {/* LEFT COLUMN */}
              <Grid size={{ xs: 12, lg: 5 }}>
                <Stack spacing={3}>
                  {/* Section 1: General Information */}
                  <Paper
                    elevation={0}
                    sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        '&::before': {
                          content: '""',
                          display: 'block',
                          width: 4,
                          height: 20,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                          mr: 1.5
                        }
                      }}
                    >
                      {t('general.generalInfo', 'General Information')}
                    </Typography>
                    <Grid container spacing={2.5}>
                      <Grid size={{ xs: 12 }}>
                        <PickerField
                          label={`${t('general.company', 'Company')} *`}
                          value={companyId}
                          {...companyPickerParams}
                          options={combinedCompanyOptions}
                          onChange={(opt) => {
                            const val = typeof opt === 'object' ? opt?.value : opt;
                            const label = typeof opt === 'object' ? opt?.label : '';
                            setCompanyId(val != null ? String(val) : '');
                            setCompanyName(label || opt?.name || '');
                            if (formErrors.company) {
                              setFormErrors((prev) => ({ ...prev, company: null }));
                            }
                          }}
                          dialogTitle={t('masterCountry.selectCompany', 'Select Company')}
                          error={!!formErrors.company}
                          helperText={formErrors.company}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          id="score-config-name"
                          label={`${t('general.name', 'Name')} *`}
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: null }));
                          }}
                          error={!!formErrors.name}
                          helperText={formErrors.name}
                          placeholder={t('driverScore.namePlaceholder', 'Example: Standard Driver Score Config')}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          id="score-config-min-dist"
                          label={`${t('driverScore.minDistance', 'Minimum Distance')} *`}
                          type="number"
                          inputProps={{ min: 0, step: 'any' }}
                          value={minDistKm}
                          onChange={(e) => {
                            setMinDistKm(e.target.value);
                            if (formErrors.min_dist_km) setFormErrors((prev) => ({ ...prev, min_dist_km: null }));
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">km</InputAdornment>
                          }}
                          error={!!formErrors.min_dist_km}
                          helperText={formErrors.min_dist_km}
                          placeholder={t('driverScore.minDistPlaceholder', 'Example: 10')}
                        />
                      </Grid>

                      {/* Warning Threshold Slider & Numeric Input */}
                      <Grid size={{ xs: 12 }}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 3, borderRadius: 2.5, bgcolor: 'background.default', borderStyle: 'dashed', borderColor: 'divider' }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1.5,
                              flexWrap: 'wrap',
                              gap: 1
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {t('driverScore.warningThreshold', 'Warning Threshold')} *
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('driverScore.sliderHint', 'Slide or manually enter the percentage (0.00% - 100.00%)')}
                              </Typography>
                            </Box>
                            <TextField
                              size="small"
                              label={`${t('driverScore.limit', 'Threshold')} (%)`}
                              value={pctWarnLimitInput}
                              onChange={handlePctInputChange}
                              onBlur={handlePctInputBlur}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                              }}
                              sx={{ width: 140 }}
                              error={!!formErrors.pct_warn_limit}
                            />
                          </Box>

                          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                            <Slider
                              value={typeof pctWarnLimit === 'number' ? pctWarnLimit : 0}
                              min={0}
                              max={100}
                              step={0.01}
                              valueLabelDisplay="auto"
                              slots={{
                                valueLabel: SliderTooltip
                              }}
                              marks={[
                                { value: 0, label: '0%' },
                                { value: 25, label: '25%' },
                                { value: 50, label: '50%' },
                                { value: 75, label: '75%' },
                                { value: 100, label: '100%' }
                              ]}
                              onChange={handleSliderChange}
                            />
                          </Box>

                          {formErrors.pct_warn_limit && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                              {formErrors.pct_warn_limit}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Section 3: Score Indexes Table */}
                  <Paper
                    elevation={0}
                    sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            '&::before': {
                              content: '""',
                              display: 'block',
                              width: 4,
                              height: 20,
                              bgcolor: 'secondary.main',
                              borderRadius: 1,
                              mr: 1.5
                            }
                          }}
                        >
                          {t('driverScore.scoreIndexes', 'Score Indexes')} *
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('driverScore.scoreIndexesHint', 'Score range mapping to Index. Ranges must be valid and cannot overlap.')}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleAddIndexRow}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('driverScore.addIndex', 'Add Index')}
                      </Button>
                    </Box>

                    {validateScoreIndexes && (
                      <Alert severity="warning" sx={{ mb: 2, py: 0.5, fontSize: '0.85rem' }}>
                        {validateScoreIndexes}
                      </Alert>
                    )}

                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ borderRadius: 2.5, borderStyle: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
                    >
                      <Table size="small" sx={{ minWidth: 400 }}>
                        <TableHead sx={{ bgcolor: 'background.default' }}>
                          <TableRow>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: 700, width: 64, py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}
                            >
                              {t('driverScore.indexLabel', 'Index')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                              {t('driverScore.minScore', 'Min Score')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                              {t('driverScore.maxScore', 'Max Score')}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: 700, width: 64, py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}
                            >
                              {t('general.action', 'Action')}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {scoreIndexes.map((row, idx) => {
                            const isRowInverted = Number(row.min_idx) > Number(row.max_idx);
                            return (
                              <TableRow
                                key={idx}
                                hover
                                sx={{
                                  bgcolor: isRowInverted ? 'rgba(211, 47, 47, 0.05)' : 'inherit',
                                  transition: 'all 0.2s',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <TableCell align="center">
                                  {/* Index label: fixed, read-only badge — no wide editable input needed */}
                                  <Chip label={row.idx_name} size="small" sx={{ fontWeight: 700, minWidth: 32 }} />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                    value={row.min_idx}
                                    onChange={(e) => handleIndexChange(idx, 'min_idx', e.target.value)}
                                    error={isRowInverted || row.min_idx < 0 || row.min_idx > 100}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                    value={row.max_idx}
                                    onChange={(e) => handleIndexChange(idx, 'max_idx', e.target.value)}
                                    error={isRowInverted || row.max_idx < 0 || row.max_idx > 100}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={scoreIndexes.length <= 1}
                                    onClick={() => handleDeleteIndexRow(idx)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Stack>
              </Grid>

              {/* RIGHT COLUMN: Section 2 — 10 Score Event Parameters (Weights) */}
              <Grid size={{ xs: 12, lg: 7 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          '&::before': {
                            content: '""',
                            display: 'block',
                            width: 4,
                            height: 20,
                            bgcolor: 'warning.main',
                            borderRadius: 1,
                            mr: 1.5
                          }
                        }}
                      >
                        {t('driverScore.eventParameters', 'Event Score Parameters (Weight %)')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('driverScore.eventParametersHint', 'Define percentage weights for all 10 driver score event types.')}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutoFixHighIcon />}
                        onClick={handleDistributeEvenly}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('driverScore.distributeEvenly', 'Distribute Evenly (10% each)')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={handleResetParams}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('driverScore.reset', 'Reset (0%)')}
                      </Button>
                    </Stack>
                  </Box>

                  {/* Total Weight Progress */}
                  <Box
                    sx={{
                      mb: 4,
                      p: 3,
                      borderRadius: 3,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.02)'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 700 }}>
                        {t('driverScore.totalParameterWeight', 'Total Parameter Weight')}
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h5" sx={{ color: isParamWeight100 ? 'success.main' : 'warning.main', fontWeight: 800 }}>
                          {Number(totalParamWeight).toFixed(2)}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: isParamWeight100 ? 'success.main' : 'text.secondary', fontWeight: 600 }}>
                          {isParamWeight100
                            ? t('driverScore.balanced100', '✓ Balanced at 100%')
                            : `${t('driverScore.diff100', 'Difference from 100%:')} ${(100 - totalParamWeight).toFixed(2)}%`}
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(0, totalParamWeight))}
                      sx={{ height: 10, borderRadius: 5, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { borderRadius: 5 } }}
                      color={isParamWeight100 ? 'success' : totalParamWeight > 100 ? 'error' : 'primary'}
                    />
                  </Box>

                  {/* Parameter cards grouped by source */}
                  <Stack spacing={4}>
                    {SOURCE_GROUPS.map((group) => {
                      const groupItems = EVENT_TYPE_OPTIONS.filter((item) => item.source === group.value);
                      if (groupItems.length === 0) return null;
                      const groupWeight = groupItems.reduce((acc, item) => acc + Number(parameters[item.value] || 0), 0);

                      return (
                        <Box key={group.value}>
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                color: `${group.color}.main`,
                                display: 'flex',
                                alignItems: 'center',
                                '&::before': {
                                  content: '""',
                                  display: 'block',
                                  width: 4,
                                  height: 16,
                                  bgcolor: `${group.color}.main`,
                                  borderRadius: 1,
                                  mr: 1
                                }
                              }}
                            >
                              {t(group.labelKey, group.label)}
                            </Typography>
                            <Divider sx={{ flex: 1, borderStyle: 'dashed' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('driverScore.groupSubtotal', 'Subtotal')}:{' '}
                              <Box component="span" sx={{ color: 'text.primary', ml: 0.5 }}>
                                {groupWeight.toFixed(2)}%
                              </Box>
                            </Typography>
                          </Stack>

                          <Grid container spacing={2}>
                            {groupItems.map((item) => (
                              <Grid key={item.value} size={{ xs: 12, sm: 6, md: group.gridMd }}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: '#fafafa',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    height: '100%',
                                    '&:hover': {
                                      bgcolor: 'background.paper',
                                      borderColor: `${item.color}.main`,
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: `${item.color}.main`,
                                        mr: 1.5,
                                        boxShadow: `0 0 0 3px rgba(0,0,0,0.04)`
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      fontWeight="700"
                                      color="text.primary"
                                      noWrap
                                      title={t(item.labelKey, item.label)}
                                    >
                                      {t(item.labelKey, item.label)}
                                    </Typography>
                                  </Box>

                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="0"
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.1, style: { fontWeight: 600 } }}
                                    value={parameters[item.value] === 0 ? '' : parameters[item.value]}
                                    onChange={(e) => handleParamChange(item.value, e.target.value)}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Typography variant="caption" fontWeight="bold">
                                            %
                                          </Typography>
                                        </InputAdornment>
                                      ),
                                      sx: { bgcolor: 'background.paper', borderRadius: 2 }
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: 'divider' },
                                        '&:hover fieldset': { borderColor: `${item.color}.main` },
                                        '&.Mui-focused fieldset': { borderColor: `${item.color}.main`, borderWidth: 2 }
                                      }
                                    }}
                                  />
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </FormDialog>
  );
}

AddScoreConfigModal.propTypes = {
  open: PropTypes.bool.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialData: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};
